import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Navbar from '../../components/navbar/navbar'
import Footer from '../../components/footer'
import { API_BASE, getImageUrl } from '../../utils/api'
import api from '../../utils/api'
import InquirySidebar from '../project/sections/InquirySidebar'
import { useAuth } from '../../context/AuthContext'
import { toast } from 'react-toastify'
import ComparePanel from '../project/sections/ComparePanel'
interface LandmarkCategory {
    _id: string;
    name: string;
    icon: string;
}

interface NearbyLoc {
    name: string;
    km: string;
    image: string;
    category?: LandmarkCategory;
}

interface DetailPair {
    label: string;
    value: string;
    icon?: string;
}

interface Locality {
    _id: string;
    name: string;
    slug: string;
    city: { name: string } | string;
    image: string;
    images: string[];
    nearbyLocations: NearbyLoc[];
    description: string;
    additionalDetails: DetailPair[];
    avgPricePerSqFt: number;
    oldAvgPricePerSqFt?: number;
    growthPercentage?: number;
    growthScore?: number;
    priceHistory?: { month: string; price: number }[];
}

interface Property {
    _id: string
    title: string
    price: number
    city?: string
    address?: string
    status: string
    images?: string[]
}

interface Project {
    _id: string
    name: string
    city?: string
    location?: string
    priceRange?: string
    status?: string;
    developer?: { name: string };
    overview?: { priceRange?: string; configuration?: string; avgPricePerSqFt?: string }
    images?: string[]
    featuredImage?: string
    propertyType?: string
    slug?: string
}

export default function LocationDetails() {
    const { slug } = useParams<{ slug: string }>()
    const scrollRef = React.useRef<HTMLDivElement>(null)
    const [locality, setLocality] = useState<Locality | null>(null)
    const [projects, setProjects] = useState<Project[]>([])
    const [properties, setProperties] = useState<Property[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'projects' | 'properties'>('projects')
    const [isExpanded, setIsExpanded] = useState(false)
    const [isHighlightsExpanded, setIsHighlightsExpanded] = useState(false)
    const [trendData, setTrendData] = useState<any>(null)
    const [computedAvgPrice, setComputedAvgPrice] = useState<number>(0)
    const [selectedImg, setSelectedImg] = useState<string | null>(null)
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all')
    const [categories, setCategories] = useState<LandmarkCategory[]>([])
    const [showAllLandmarks, setShowAllLandmarks] = useState(false)

    const [inquiryForm, setInquiryForm] = useState({ 
        name: '', 
        email: '', 
        phone: '', 
        agreeContact: false, 
        agreeTerms: false,
        interestedHomeLoan: true,
        purpose: 'Buy',
        propertyType: '',
        propertySubType: ''
    });
    const [inquirySubmitting, setInquirySubmitting] = useState(false);
    const [inquirySuccess, setInquirySuccess] = useState('');
    const [inquiryError, setInquiryError] = useState('');
    
    const { token } = useAuth();
    const [wishlistIds, setWishlistIds] = useState<string[]>([]);
    const [isCompareOpen, setIsCompareOpen] = useState(false);
    const [compareList, setCompareList] = useState<Project[]>([]);

    useEffect(() => {
        const stored = localStorage.getItem('compareList');
        if (stored) {
            try { setCompareList(JSON.parse(stored)); } catch (e) { }
        }
        const handleCompareUpdate = () => {
            const updated = localStorage.getItem('compareList');
            if (updated) {
                try { setCompareList(JSON.parse(updated)); } catch (e) { }
            }
        };
        window.addEventListener('compareUpdated', handleCompareUpdate);
        return () => window.removeEventListener('compareUpdated', handleCompareUpdate);
    }, []);

    useEffect(() => {
        if (token) {
            api.getWishlist(token).then(data => {
                if (data.projectWishlist || data.propertyWishlist) {
                    const ids: string[] = [];
                    if (data.projectWishlist) ids.push(...data.projectWishlist.map((p: any) => p._id));
                    if (data.propertyWishlist) ids.push(...data.propertyWishlist.map((p: any) => p._id));
                    setWishlistIds(ids);
                }
            }).catch(console.error);
        } else {
            setWishlistIds([]);
        }
    }, [token]);

    const handleToggleWishlist = async (e: React.MouseEvent, itemId: string, itemType: 'project' | 'property' = 'project') => {
        e.preventDefault();
        e.stopPropagation();
        if (!token) {
            if (window.confirm("Need to login to save items to wishlist. Go to login page?")) {
                window.location.href = '/login';
            }
            return;
        }
        try {
            const res = await api.toggleWishlist(token, itemId, itemType);
            if (res.isSaved) {
                setWishlistIds(prev => [...prev, itemId]);
                toast.success("Added to wishlist");
            } else {
                setWishlistIds(prev => prev.filter(id => id !== itemId));
                toast.info("Removed from wishlist");
            }
        } catch (error) {
            console.error("Wishlist toggle error:", error);
            toast.error("Failed to update wishlist");
        }
    };

    const removeFromCompare = (id: string) => {
        const updated = compareList.filter((p: any) => p._id !== id);
        setCompareList(updated as Project[]);
        localStorage.setItem('compareList', JSON.stringify(updated));
        window.dispatchEvent(new Event('compareUpdated'));
    };

    const handleCompare = (e: React.MouseEvent, item: any) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const existing = localStorage.getItem('compareList');
            let list = existing ? JSON.parse(existing) : [];
            if (!list.find((p: any) => p._id === item._id)) {
                if (list.length >= 3) {
                    alert('You can compare up to 3 properties at a time.');
                } else {
                    list.push(item);
                    localStorage.setItem('compareList', JSON.stringify(list));
                    window.dispatchEvent(new Event('compareUpdated'));
                }
            }
            setIsCompareOpen(true);
        } catch (error) {
            console.error('Error adding to compare:', error);
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const { scrollLeft, clientWidth } = scrollRef.current
            const scrollAmount = clientWidth * 0.8
            scrollRef.current.scrollTo({
                left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
                behavior: 'smooth'
            })
        }
    }

    useEffect(() => {
        if (!slug) return
        setLoading(true)
        
        fetch(`${API_BASE}/localities/slug/${slug}`)
            .then(r => r.json())
            .then(data => {
                if (data._id) {
                    setLocality(data)
                    
                    // Extract unique categories from nearbyLocations
                    const cats: LandmarkCategory[] = []
                    const seen = new Set()
                    data.nearbyLocations?.forEach((nb: any) => {
                        if (nb.category && !seen.has(nb.category._id)) {
                            seen.add(nb.category._id)
                            cats.push(nb.category)
                        }
                    })
                    setCategories(cats)
                    if (cats.length > 0) setSelectedCategoryId('all')
                    // Fetch projects and properties for this locality
                    const searchName = data.name
                    
                    // Fetch projects
                    fetch(`${API_BASE}/projects`)
                        .then(pr => pr.json())
                        .then(pData => {
                            const list = Array.isArray(pData) ? pData : (pData.projects ?? [])
                            const filtered = list.filter((p: any) => 
                                p.location?.toLowerCase().includes(searchName.toLowerCase()) ||
                                p.city?.toLowerCase().includes(searchName.toLowerCase()) ||
                                p.name?.toLowerCase().includes(searchName.toLowerCase())
                            )
                            setProjects(filtered)

                            // Compute Market Val from Approved projects only
                            const parsePrice = (str?: string): number => {
                                if (!str) return 0;
                                const parts = str.split(/[–\-]/);
                                if (parts.length > 1) {
                                    const v1 = parseInt(parts[0].replace(/[^0-9]/g, ''), 10);
                                    const v2 = parseInt(parts[1].replace(/[^0-9]/g, ''), 10);
                                    if (!isNaN(v1) && !isNaN(v2) && v1 > 0 && v2 > 0) return Math.round((v1 + v2) / 2);
                                    return (!isNaN(v1) && v1 > 0) ? v1 : 0;
                                }
                                const p = parseInt(str.replace(/[^0-9]/g, ''), 10);
                                return isNaN(p) ? 0 : p;
                            };
                            const approvedPrices = filtered
                                .filter((p: any) => p.status === 'Approved')
                                .map((p: any) => parsePrice(p.overview?.avgPricePerSqFt))
                                .filter((v: number) => v > 0);
                            const avg = approvedPrices.length > 0
                                ? Math.round(approvedPrices.reduce((a: number, b: number) => a + b, 0) / approvedPrices.length)
                                : 0;
                            setComputedAvgPrice(avg);
                        })

                    // Fetch properties
                    fetch(`${API_BASE}/properties`)
                        .then(pp => pp.json())
                        .then(ppData => {
                            const list = Array.isArray(ppData) ? ppData : (ppData.properties ?? [])
                            setProperties(list.filter((p: any) => 
                                p.address?.toLowerCase().includes(searchName.toLowerCase()) ||
                                p.city?.toLowerCase().includes(searchName.toLowerCase())
                            ))
                        })
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false))

        // Fetch Trend Data
        fetch(`${API_BASE}/locality-trends/slug/${slug}`)
            .then(r => r.json())
            .then(data => {
                if (data.locality) {
                    setTrendData(data)
                }
            })
            .catch(err => console.error("Error fetching trend data:", err))
    }, [slug])

    const handleInquirySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inquiryForm.name || !inquiryForm.phone) {
            setInquiryError('Name and Phone number are required.');
            return;
        }
        setInquirySubmitting(true);
        setInquiryError('');
        setInquirySuccess('');
        try {
            const res = await fetch(`${API_BASE}/bookings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    locality: locality?._id,
                    type: 'Locality Inquiry',
                    name: inquiryForm.name,
                    phone: inquiryForm.phone,
                    email: inquiryForm.email,
                    message: `General inquiry for ${locality?.name || 'locality'}${inquiryForm.interestedHomeLoan ? ' (Interested in Home Loans)' : ''}`,
                    agreeContact: inquiryForm.agreeContact,
                    interestedHomeLoan: inquiryForm.interestedHomeLoan,
                    purpose: inquiryForm.purpose,
                    propertyType: inquiryForm.propertyType,
                    propertySubType: inquiryForm.propertySubType
                })
            });
            const data = await res.json();
            if (res.ok) {
                setInquirySuccess('Your inquiry has been submitted! We will contact you soon.');
                setInquiryForm({ 
                    name: '', 
                    email: '', 
                    phone: '', 
                    agreeContact: false, 
                    agreeTerms: false,
                    interestedHomeLoan: true,
                    purpose: 'Buy',
                    propertyType: '',
                    propertySubType: ''
                });
                setTimeout(() => setInquirySuccess(''), 5000);
            } else {
                setInquiryError(data.message || 'Failed to submit inquiry.');
            }
        } catch {
            setInquiryError('Something went wrong. Please try again.');
        } finally {
            setInquirySubmitting(false);
        }
    };

    const handleNextImg = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (!locality?.images || !selectedImg) return;
        const currentIndex = locality.images.indexOf(selectedImg);
        const nextIndex = (currentIndex + 1) % locality.images.length;
        setSelectedImg(locality.images[nextIndex]);
    };

    const handlePrevImg = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (!locality?.images || !selectedImg) return;
        const currentIndex = locality.images.indexOf(selectedImg);
        const prevIndex = (currentIndex - 1 + locality.images.length) % locality.images.length;
        setSelectedImg(locality.images[prevIndex]);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!selectedImg) return;
            if (e.key === 'ArrowRight') handleNextImg();
            if (e.key === 'ArrowLeft') handlePrevImg();
            if (e.key === 'Escape') setSelectedImg(null);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImg, locality?.images]);

    const renderNearbyIcon = (nb: any) => {
        const nameLower = (nb.name || '').toLowerCase();
        const catLower = (nb.category?.name || '').toLowerCase();
        
        let iconClass = 'fa-location-dot';
        if (nameLower.includes('bank') || nameLower.includes('atm') || catLower.includes('bank')) iconClass = 'fa-building-columns';
        else if (nameLower.includes('hospital') || nameLower.includes('clinic') || nameLower.includes('medical') || catLower.includes('hospital')) iconClass = 'fa-hospital';
        else if (nameLower.includes('school') || nameLower.includes('academy') || nameLower.includes('college') || nameLower.includes('university') || catLower.includes('school') || catLower.includes('education')) iconClass = 'fa-graduation-cap';
        else if (nameLower.includes('mall') || nameLower.includes('market') || nameLower.includes('plaza') || nameLower.includes('hub') || catLower.includes('shopping') || nameLower.includes('dmart') || nameLower.includes('mart')) iconClass = 'fa-basket-shopping';
        else if (nameLower.includes('station') || nameLower.includes('metro') || nameLower.includes('bus') || catLower.includes('transport')) iconClass = 'fa-bus';
        else if (nameLower.includes('restaurant') || nameLower.includes('cafe') || catLower.includes('food')) iconClass = 'fa-utensils';
        else if (nameLower.includes('park') || nameLower.includes('garden') || catLower.includes('entertainment') || catLower.includes('park')) iconClass = 'fa-tree';
        else if (nameLower.includes('airport') || catLower.includes('airport')) iconClass = 'fa-plane';
        else if (nameLower.includes('hotel') || catLower.includes('hotel')) iconClass = 'fa-hotel';
        else if (nameLower.includes('gym') || nameLower.includes('fitness') || catLower.includes('gym')) iconClass = 'fa-dumbbell';
        else if (nameLower.includes('theater') || nameLower.includes('cinema') || catLower.includes('theater')) iconClass = 'fa-film';

        if (nb.category?.icon && nb.category.icon.startsWith('fa-')) {
            iconClass = nb.category.icon;
        }

        return (
            <div className="nb-thumb me-3 d-flex align-items-center justify-content-center bg-blue-50 bg-opacity-20 rounded-lg text-primary overflow-hidden" style={{ width: '50px', height: '50px', flexShrink: 0, border: '1px solid rgba(5,20,75,0.08)', background: '#f8fafc' }}>
                <i className={`fa-solid ${iconClass}`} style={{ fontSize: '20px', color: '#05144b', fontFamily: '"Font Awesome 6 Free", "Font Awesome 5 Free", "FontAwesome"', fontWeight: '900' }}></i>
            </div>
        );
    };

    if (loading) return <FullPageLoading />
    if (!locality) return <NotFoundState />

    return (
        <div className="location-details-page-wrapper" style={{ backgroundColor: '#fcfdff', minHeight: '100vh', fontFamily: 'Figtree, "Figtree Fallback", sans-serif' }}>
            <ComparePanel
                isCompareOpen={isCompareOpen}
                setIsCompareOpen={setIsCompareOpen}
                compareList={compareList}
                removeFromCompare={removeFromCompare}
                getImageUrl={getImageUrl}
            />
            <style>{`
                .location-details-page-wrapper, 
                .location-details-page-wrapper *,
                .location-details-page-wrapper h1,
                .location-details-page-wrapper h2,
                .location-details-page-wrapper h3,
                .location-details-page-wrapper h4,
                .location-details-page-wrapper h5,
                .location-details-page-wrapper h6,
                .location-details-page-wrapper p,
                .location-details-page-wrapper span,
                .location-details-page-wrapper div,
                .location-details-page-wrapper button,
                .location-details-page-wrapper input,
                .location-details-page-wrapper textarea {
                    font-family: Figtree, "Figtree Fallback", sans-serif !important;
                }
                .location-details-page-wrapper i {
                    font-family: "Font Awesome 6 Free", "Font Awesome 5 Free", "FontAwesome" !important;
                }
            `}</style>
            <Navbar transparent={false} />

            {/* Premium Hero Banner */}
            <div className="position-relative hero-container" style={{ height: '60vh', minHeight: '450px' }}>
                <div className="absolute-full overflow-hidden">
                    <motion.img 
                        initial={{ scale: 1.1 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse' }}
                        src={getImageUrl(locality.image)} 
                        alt={locality.name} 
                        className="w-100 h-100 object-fit-cover" 
                    />
                    <div className="absolute-full" style={{ background: 'linear-gradient(to bottom, rgba(5,20,75,0.4), rgba(5,20,75,0.85))' }}></div>
                </div>

                <div className="container h-100 position-relative d-flex flex-column justify-content-center text-white">
                    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <span className="badge-premium mb-3">
                            <i className="fa-solid fa-location-dot me-2"></i> 
                            {(locality.city && typeof locality.city === 'object') ? (locality.city as any).name : 'Location'}, Gujarat
                        </span>
                        <h1 className="display-2 fw-black mb-3 text-white" style={{ 
                            textShadow: '2px 2px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' 
                        }}>
                            {locality.name}
                        </h1>
                        <p className="lead opacity-100 max-w-600 mb-4 fw-medium">
                            Explore the finest real estate opportunities and the vibrant lifestyle of {locality.name}.
                        </p>
                        
                        <div className="d-flex flex-wrap gap-3">
                            <div className="glass-card shadow-navy border border-light border-opacity-10 px-4 py-3 rounded-xl d-flex align-items-center">
                                <i className="fa-solid fa-building-circle-check me-3 text-warning fs-4"></i>
                                <div>
                                    <div className="fw-bold h5 mb-0">{projects.length}</div>
                                    <div className="small text-white-50 font-bold" style={{ fontSize: '10px' }}>Projects</div>
                                </div>
                            </div>
                            <div className="glass-card shadow-navy border border-light border-opacity-10 px-4 py-3 rounded-xl d-flex align-items-center">
                                <i className="fa-solid fa-house-chimney me-3 text-warning fs-4"></i>
                                <div>
                                    <div className="fw-bold h5 mb-0">{properties.length}</div>
                                    <div className="small text-white-50 font-bold" style={{ fontSize: '10px' }}>Listings</div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
                
                {/* Decorative Wave */}
                <div className="position-absolute bottom-0 start-0 w-100" style={{ height: '50px', background: 'linear-gradient(to top, #fcfdff, transparent)' }}></div>
            </div>

            <div className="container-fluid px-3 px-md-5 py-5" style={{ marginTop: '-60px', position: 'relative', zIndex: 10 }}>
                <div className="row g-4">
                    
                    {/* Left Column: Analysis & Info */}
                    <div className="col-lg-8">
                        
                        {/* Market Trends Section */}
                        <motion.section 
                            className="bg-white rounded-xl shadow-navy p-3 p-md-4 mb-3 border border-light overflow-hidden"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4 gap-4">
                                <div className="d-flex align-items-center">
                                    <div className="icon-box-primary me-3 bg-navy text-white">
                                        <i className="fa-solid fa-chart-line"></i>
                                    </div>
                                    <div>
                                        <h3 className="fw-bold mb-0 text-navy fs-4 fs-md-3">Price Trends</h3>
                                        <p className="text-black small mb-0 fw-bold">Market dynamics in {locality.name}</p>
                                    </div>
                                </div>
                                <div className="d-flex gap-4 justify-content-start justify-content-sm-end">
                                    <div className="text-start text-sm-end">
                                        <div className="text-black small font-bold" style={{ fontSize: '10px' }}>Avg. Price</div>
                                        <div className="h5 fw-black mb-0 text-black">₹{(computedAvgPrice || locality.avgPricePerSqFt || 4500).toLocaleString('en-IN')}<span className="small text-black fw-bold">/sq.ft</span></div>
                                        {computedAvgPrice > 0 && (
                                            <div style={{ fontSize: '9px', fontWeight: 700, color: '#3b82f6', marginTop: '2px' }}>
                                                <i className="fa-solid fa-building" style={{ marginRight: '3px' }}></i>
                                                Projects Avg.
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-start text-sm-end">
                                        <div className="text-black small font-bold" style={{ fontSize: '10px' }}>YoY Growth</div>
                                        <div className="h5 fw-black mb-0 text-black">+{trendData?.locality?.growthPercentage || '12.5'}%</div>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-4">
                                <div className="col-lg-12">
                                    <div style={{ height: '250px', width: '100%' }} className="pe-2">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart 
                                                data={[2022, 2023, 2024, 2025, 2026].map(year => ({
                                                    year,
                                                    price: Math.round((computedAvgPrice || locality.avgPricePerSqFt || 4500) * (1 - (2026 - year) * 0.06))
                                                }))}
                                                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                            >
                                                <defs>
                                                    <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#05144b" stopOpacity={0.1}/>
                                                        <stop offset="95%" stopColor="#05144b" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                <XAxis 
                                                    dataKey="year" 
                                                    axisLine={false} 
                                                    tickLine={false} 
                                                    tick={{ fill: '#000000', fontSize: 13, fontWeight: 800 }}
                                                    dy={10}
                                                />
                                                <YAxis 
                                                    axisLine={false} 
                                                    tickLine={false} 
                                                    tick={{ fill: '#000000', fontSize: 11, fontWeight: 800 }}
                                                    tickFormatter={(v) => `₹${v/1000}k`}
                                                />
                                                <Tooltip 
                                                    contentStyle={{ borderRadius: '15px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', padding: '15px' }}
                                                    itemStyle={{ color: '#000', fontWeight: '900' }}
                                                    labelStyle={{ color: '#000', marginBottom: '5px', fontSize: '13px', fontWeight: '800' }}
                                                    formatter={(value: any) => [`₹${value.toLocaleString('en-IN')} /sq.ft`, 'Market Rate']}
                                                />
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="price" 
                                                    stroke="#05144b" 
                                                    strokeWidth={4} 
                                                    fillOpacity={1} 
                                                    fill="url(#colorPrice)"
                                                    dot={{ r: 4, fill: '#fff', stroke: '#05144b', strokeWidth: 2 }}
                                                    activeDot={{ r: 6, fill: '#fdcc0a', stroke: '#05144b', strokeWidth: 2 }}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        </motion.section>

                        {/* Area Overview */}
                        <motion.section 
                            className="bg-white rounded-xl shadow-navy p-3 p-md-4 mb-3 border border-light"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="d-flex align-items-center mb-4">
                                <div className="icon-box-primary me-3">
                                    <i className="fa-solid fa-circle-info"></i>
                                </div>
                                <h3 className="fw-bold mb-0 text-navy">About {locality.name}</h3>
                            </div>
                            <div className={`rich-text-container relative ${!isExpanded ? 'content-clamped' : ''}`}>
                                <div className="rich-text-content" style={{ 
                                    textAlign: 'justify', 
                                    fontSize: '1.05rem', 
                                    fontFamily: 'Figtree, "Figtree Fallback", sans-serif', 
                                    color: '#000000',
                                    fontWeight: '500',
                                    lineHeight: '1.8',
                                    transition: 'all 0.5s ease'
                                }}>
                                    {locality.description ? (
                                        <div dangerouslySetInnerHTML={{ __html: locality.description }}></div>
                                    ) : (
                                        <p>Discover the potential of {locality.name}, a prime locality offering a mix of premium urban living and strategic connectivity.</p>
                                    )}
                                </div>
                                {!isExpanded && (
                                    <div className="content-fade-overlay"></div>
                                )}
                            </div>

                            <div className="mt-4">
                                <button 
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    className="btn-read-more"
                                >
                                    {isExpanded ? (
                                        <><span>Show Less</span> <i className="fa-solid fa-chevron-up ms-2"></i></>
                                    ) : (
                                        <><span>Read More</span> <i className="fa-solid fa-chevron-down ms-2"></i></>
                                    )}
                                </button>
                            </div>

                            <style>{`
                                .shadow-navy {
                                    box-shadow: 0 15px 40px rgba(5, 20, 75, 0.12) !important;
                                }
                                .rich-text-container.content-clamped {
                                    max-height: 165px; /* Roughly 5 lines based on 1.8 line-height */
                                    overflow: hidden;
                                }
                                .content-fade-overlay {
                                    position: absolute;
                                    bottom: 0;
                                    left: 0;
                                    width: 100%;
                                    height: 80px;
                                    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.95));
                                    pointer-events: none;
                                }
                                .btn-read-more {
                                    border: none;
                                    background: #05144b;
                                    color: white;
                                    font-weight: 800;
                                    padding: 10px 24px;
                                    border-radius: 50px;
                                    display: flex;
                                    align-items: center;
                                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                                    box-shadow: 0 4px 15px rgba(5, 20, 75, 0.2);
                                }
                                .btn-read-more:hover {
                                    background: #fdcc0a;
                                    color: black;
                                    transform: translateY(-2px);
                                    box-shadow: 0 8px 20px rgba(253, 204, 10, 0.3);
                                }
                                .rich-text-content h1, .rich-text-content h2, .rich-text-content h3 {
                                    color: #05144b;
                                    font-weight: 800;
                                    margin-top: 1.5rem;
                                    margin-bottom: 1rem;
                                    text-transform: none;
                                }
                                .rich-text-content h3 { font-size: 1.5rem; }
                                .rich-text-content p {
                                    margin-bottom: 1.2rem;
                                }
                                .rich-text-content ul, .rich-text-content ol {
                                    margin-bottom: 1.5rem;
                                    padding-left: 1.5rem;
                                }
                                .rich-text-content li {
                                    margin-bottom: 0.5rem;
                                    position: relative;
                                }
                                .rich-text-content ul li::before {
                                    content: "•";
                                    color: #fdcc0a;
                                    font-weight: bold;
                                    display: inline-block; 
                                    width: 1em;
                                    margin-left: -1em;
                                }
                                .rich-text-content blockquote {
                                    border-left: 4px solid #fdcc0a;
                                    padding-left: 1.5rem;
                                    font-style: italic;
                                    margin: 1.5rem 0;
                                    background: #f8fafc;
                                    padding: 1rem 1.5rem;
                                    border-radius: 0 1rem 1rem 0;
                                }
                                .rich-text-content a {
                                    color: #05144b;
                                    text-decoration: underline;
                                    font-weight: 700;
                                }
                            `}</style>
                        </motion.section>

                        {/* Locality Highlights Card */}
                        {locality.additionalDetails?.length > 0 && (
                            <motion.section 
                                className="bg-white rounded-xl shadow-navy p-3 p-md-4 mb-3 border border-light"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                            >
                                <div className="d-flex align-items-center mb-4">
                                    <div className="icon-box-primary me-3 bg-navy text-white">
                                        <i className="fa-solid fa-list-check"></i>
                                    </div>
                                    <h5 className="fw-bold mb-0 text-navy">Locality Highlights</h5>
                                </div>
                                <div className="row g-3" style={{ fontFamily: 'Figtree, "Figtree Fallback", sans-serif' }}>
                                    {(isHighlightsExpanded ? locality.additionalDetails : locality.additionalDetails.slice(0, 6)).map((det, i) => (
                                        <div key={i} className="col-sm-6 col-lg-6">
                                            <div className="p-3 rounded-xl bg-white border border-light h-100 shadow-navy transition-all d-flex align-items-start gap-4">
                                                <div className="flex-shrink-0 d-flex align-items-center justify-content-center bg-blue-50 bg-opacity-30 rounded-lg p-2 overflow-hidden" style={{ width: '80px', height: '80px' }}>
                                                    {det.icon && (det.icon.startsWith('fa-') || !det.icon.includes('/')) ? (
                                                        <i className={`fa-solid ${det.icon || 'fa-star'} text-rose-500`} style={{ fontSize: '40px' }}></i>
                                                    ) : det.icon ? (
                                                        <img src={getImageUrl(det.icon)} alt={det.label} className="w-100 h-100 object-fit-contain" />
                                                    ) : (
                                                        <i className="fa-solid fa-star text-rose-500" style={{ fontSize: '40px' }}></i>
                                                    )}
                                                </div>
                                                <div className="flex-grow-1 pt-1">
                                                    <div className="text-navy mb-2" style={{ fontSize: '11px', fontWeight: '900' }}>{det.label}</div>
                                                    <div className="text-navy lh-base" style={{ fontSize: '19px', fontWeight: '600', color: '#05144b' }}>{det.value}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {locality.additionalDetails.length > 6 && (
                                    <div className="mt-5 text-center">
                                        <button 
                                            onClick={() => setIsHighlightsExpanded(!isHighlightsExpanded)}
                                            className="btn btn-outline-navy rounded-pill px-5 py-2 fw-bold shadow-sm transition-all"
                                            style={{ fontSize: '12px' }}
                                        >
                                            {isHighlightsExpanded ? (
                                                <><span>Show Less</span> <i className="fa-solid fa-chevron-up ms-2"></i></>
                                            ) : (
                                                <><span>View More Highlights</span> <i className="fa-solid fa-chevron-down ms-2"></i></>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </motion.section>
                        )}

                        {/* Photo Gallery - Premium 3-Image Horizontal Grid */}
                        {locality.images?.length > 0 && (
                            <section className="mb-3 px-1">
                                <div className="section-header mb-4">
                                    <h4 className="fw-bold text-black">Area Showcase</h4>
                                    <div className="bar-accent"></div>
                                </div>
                                <div className="row g-3">
                                    {locality.images.slice(0, 3).map((img, i) => (
                                        <div key={i} className="col-4 col-md-4">
                                            <motion.div 
                                                whileHover={{ scale: 1.02 }} 
                                                onClick={() => setSelectedImg(img)}
                                                className="gallery-item rounded-xl overflow-hidden position-relative shadow-navy" 
                                                style={{ height: '180px', cursor: 'pointer' }}
                                            >
                                                <img src={getImageUrl(img)} alt={`Area ${i}`} className="w-100 h-100 object-fit-cover" />
                                                
                                                {/* View More Overlay on the 3rd image */}
                                                {i === 2 && locality.images.length > 3 && (
                                                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: 'rgba(5, 20, 75, 0.7)' }}>
                                                        <div className="text-center">
                                                            <div className="h4 fw-black text-white mb-0">+{locality.images.length - 2}</div>
                                                            <div className="text-white-50 fw-bold" style={{ fontSize: '10px' }}>More Photos</div>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                <div className="overlay-light"></div>
                                            </motion.div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Dynamic Tabs Section */}
                        <section className="mt-3">
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-end mb-4 border-bottom pb-2">
                                <div className="section-header">
                                    <h4 className="fw-bold text-black">Properties in this Area</h4>
                                    <p className="text-black small fw-bold">Explore hand-picked residential & commercial options</p>
                                </div>
                                <div className="tab-pill-container mt-3 mt-md-0">
                                    <button 
                                        onClick={() => setActiveTab('projects')}
                                        className={`tab-pill ${activeTab === 'projects' ? 'active' : ''}`}
                                    >
                                        <i className="fa-solid fa-building-columns me-2"></i>Projects
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('properties')}
                                        className={`tab-pill ${activeTab === 'properties' ? 'active' : ''}`}
                                    >
                                        <i className="fa-solid fa-home me-2"></i>Listings
                                    </button>
                                </div>
                            </div>

                            <div className="pt-2">
                                <AnimatePresence mode="wait">
                                    {activeTab === 'projects' ? (
                                        <motion.div 
                                            key="projects" 
                                            initial={{ opacity: 0, y: 10 }} 
                                            animate={{ opacity: 1, y: 0 }} 
                                            exit={{ opacity: 0, y: -10 }}
                                            className="row g-4"
                                        >
                                            {projects.length > 0 ? (
                                                projects.map(p => <LocalityProjectCard key={p._id} project={p} wishlistIds={wishlistIds} onToggleWishlist={handleToggleWishlist} onCompare={handleCompare} />)
                                            ) : <EmptyState message="No ongoing projects found currently." />}
                                        </motion.div>
                                    ) : (
                                        <motion.div 
                                            key="props" 
                                            initial={{ opacity: 0, y: 10 }} 
                                            animate={{ opacity: 1, y: 0 }} 
                                            exit={{ opacity: 0, y: -10 }}
                                            className="row g-4"
                                        >
                                            {properties.length > 0 ? (
                                                properties.map(p => <LocalityListingCard key={p._id} property={p} wishlistIds={wishlistIds} onToggleWishlist={handleToggleWishlist} />)
                                            ) : <EmptyState message="No properties for sale or rent found." />}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </section>
                    </div>

                    {/* Right Column: Sidebar Analysis */}
                    <aside className="col-lg-4">
                        <div className="sticky-top-layout">
                            
                            {/* Proximity Analysis with Category Tabs */}
                            {locality.nearbyLocations?.length > 0 && (
                                <motion.div 
                                    className="bg-white rounded-xl shadow-navy px-4 px-xl-5 py-5 mb-4 border border-light"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                >
                                    <h5 className="fw-bold text-black mb-4 d-flex align-items-center">
                                        <i className="fa-solid fa-compass me-2 text-warning"></i> Nearby Landmarks
                                    </h5>

                                    {/* Horizontal Category Pill Menu with Arrows */}
                                    <div className="category-scroll-wrapper mb-4">
                                        <button 
                                            onClick={() => scroll('left')}
                                            className="nav-arrow left"
                                            aria-label="Scroll Left"
                                        >
                                            <i className="fa-solid fa-chevron-left"></i>
                                        </button>
                                        
                                        <div 
                                            ref={scrollRef}
                                            className="d-flex align-items-center gap-2 overflow-x-auto px-5 pb-2 scrollbar-none scroll-smooth" 
                                            style={{ scrollSnapType: 'x mandatory' }}
                                        >
                                            <button 
                                                onClick={() => setSelectedCategoryId('all')}
                                                className={`category-pill ${selectedCategoryId === 'all' ? 'active' : ''}`}
                                            >
                                                All
                                            </button>
                                            {categories.map((cat) => (
                                                <button 
                                                    key={cat._id}
                                                    onClick={() => setSelectedCategoryId(cat._id)}
                                                    className={`category-pill ${selectedCategoryId === cat._id ? 'active' : ''}`}
                                                >
                                                    <div className="pill-icon">
                                                        {cat.icon.startsWith('fa-') ? (
                                                            <i className={`fa-solid ${cat.icon}`}></i>
                                                        ) : (
                                                            <img src={getImageUrl(cat.icon)} alt="" className="w-100 h-100 object-fit-contain" />
                                                        )}
                                                    </div>
                                                    {cat.name}
                                                </button>
                                            ))}
                                        </div>

                                        <button 
                                            onClick={() => scroll('right')}
                                            className="nav-arrow right"
                                            aria-label="Scroll Right"
                                        >
                                            <i className="fa-solid fa-chevron-right"></i>
                                        </button>
                                    </div>

                                    <div className="proximity-list">
                                        <AnimatePresence mode="popLayout">
                                            {(() => {
                                                const filtered = locality.nearbyLocations.filter(nb => selectedCategoryId === 'all' || nb.category?._id === selectedCategoryId);
                                                const visible = showAllLandmarks ? filtered : filtered.slice(0, 5);

                                                return (
                                                    <>
                                                        {visible.map((nb, i) => (
                                                            <motion.div 
                                                                key={`${nb.name}-${i}`} 
                                                                layout
                                                                initial={{ opacity: 0, x: 10 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                exit={{ opacity: 0, scale: 0.95 }}
                                                                transition={{ duration: 0.3 }}
                                                                className="d-flex align-items-center py-3 border-bottom-dashed"
                                                            >
                                                                {renderNearbyIcon(nb)}
                                                                <div className="flex-grow-1">
                                                                    <div className="fw-bold text-black small">{nb.name}</div>
                                                                    <div className="d-flex align-items-center gap-2 mt-1">
                                                                        <div className="text-black fw-bold" style={{ fontSize: '12px' }}>Distance: <span className="text-primary fw-bold">{nb.km} km</span></div>
                                                                        {nb.category && (
                                                                            <div className="text-muted italic" style={{ fontSize: '10px' }}>• {nb.category.name}</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <i className="fa-solid fa-chevron-right text-black opacity-25 fs-xs"></i>
                                                            </motion.div>
                                                        ))}
                                                        {filtered.length > 5 && (
                                                            <div className="text-center mt-3">
                                                                <button 
                                                                    onClick={() => setShowAllLandmarks(!showAllLandmarks)} 
                                                                    className="btn btn-sm btn-outline-primary rounded-pill px-4"
                                                                >
                                                                    {showAllLandmarks ? 'View Less' : `View More (${filtered.length - 5} more)`}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            )}


                            {/* Map CTA */}
                            <div className="card-map-cta rounded-xl overflow-hidden shadow-navy border border-light">
                                <div className="p-4 bg-white border-bottom">
                                    <h6 className="fw-bold text-black mb-0">Location Connectivity</h6>
                                </div>
                                <div className="position-relative" style={{ height: '200px' }}>
                                    <iframe 
                                        src={`https://maps.google.com/maps?q=${encodeURIComponent(locality.name + ', Gandhinagar')}&output=embed`} 
                                        width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                                    />
                                    <div className="position-absolute bottom-0 start-0 w-100 p-3" style={{ background: 'linear-gradient(transparent, white)' }}>
                                        <a href={`https://maps.google.com/maps?q=${encodeURIComponent(locality.name + ', Gandhinagar')}`} target="_blank" className="btn btn-sm text-white w-100 rounded-pill" style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}>View Full Map</a>
                                    </div>
                                </div>
                            </div>

                            {/* Inquiry Form */}
                            <div className="mt-4">
                                <InquirySidebar
                                    project={{ name: locality.name, city: (locality.city as any).name || locality.city }}
                                    scrollToSection={() => {}}
                                    setShowBrochureModal={() => {}}
                                    inquiryForm={inquiryForm}
                                    setInquiryForm={setInquiryForm}
                                    handleInquirySubmit={handleInquirySubmit}
                                    inquirySubmitting={inquirySubmitting}
                                    inquiryError={inquiryError}
                                    inquirySuccess={inquirySuccess}
                                    hideSiteVisit={true}
                                />
                            </div>

                        </div>
                    </aside>
                </div>
            </div>

            <AnimatePresence>
                {selectedImg && (
                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed-full z-peak bg-dark bg-opacity-95 d-flex align-items-center justify-content-center p-3"
                        onClick={() => setSelectedImg(null)}
                        style={{ cursor: 'zoom-out' }}
                    >
                        {/* Header Controls */}
                        <div className="position-absolute top-0 start-0 w-100 p-4 d-flex justify-content-between align-items-center z-10 pointer-events-none">
                            <div className="bg-gradient-to-r from-[#05144b] to-[#4c1d95] px-4 py-2 rounded-pill text-white fw-bold shadow-lg border border-white border-opacity-10" style={{ fontSize: '13px' }}>
                                <span className="text-warning">{locality.images.indexOf(selectedImg) + 1}</span> <span className="opacity-50 mx-2">/</span> {locality.images.length}
                            </div>
                            <button 
                                className="btn-close-gallery pointer-events-auto shadow-lg"
                                onClick={() => setSelectedImg(null)}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        {/* Navigation Arrows */}
                        <button 
                            className="gallery-nav-btn prev-btn shadow-lg"
                            onClick={handlePrevImg}
                        >
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                        
                        <button 
                            className="gallery-nav-btn next-btn shadow-lg"
                            onClick={handleNextImg}
                        >
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>

                        <motion.img 
                            key={selectedImg}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            src={getImageUrl(selectedImg)} 
                            className="max-h-90 max-w-95 rounded-xl shadow-2xl pointer-events-auto" 
                            alt="Preview"
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: 'default' }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <Footer />

            <style>{`
                .hero-container { position: relative; width: 100%; }
                .category-scroll-wrapper { position: relative; width: 100%; }
                .nav-arrow {
                    position: absolute;
                    top: 42%;
                    transform: translateY(-50%);
                    width: 28px;
                    height: 28px;
                    background: linear-gradient(90deg, #05144b 0%, #4c1d95 100%);
                    border: 1px solid transparent;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 10px;
                    color: #fff;
                    z-index: 10;
                    box-shadow: 0 4px 12px rgba(5,20,75,0.2);
                    cursor: pointer;
                    transition: all 0.3s;
                }
                .nav-arrow.left { left: -45px; }
                .nav-arrow.right { right: -45px; }
                .nav-arrow:hover { background: #fdcc0a; color: #05144b; border-color: #fdcc0a; transform: translateY(-50%) scale(1.1); }
                .category-scroll-container { position: relative; width: 100%; }
                .scrollbar-none::-webkit-scrollbar { display: none; }
                .scrollbar-none { -ms-overflow-style: none; scrollbar-width: none; }
                .category-pill { 
                    flex-shrink: 0; 
                    white-space: nowrap; 
                    background: #f8f9fa; 
                    border: 1px solid #eee; 
                    color: #05144b; 
                    padding: 8px 16px; 
                    border-radius: 50px; 
                    font-size: 11px; 
                    font-weight: 800; 
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                    scroll-snap-align: start;
                }
                .category-pill:hover { background: #eef1f5; transform: translateY(-1px); }
                .category-pill.active { background: linear-gradient(90deg, #05144b 0%, #4c1d95 100%); color: white; border-color: transparent; box-shadow: 0 5px 15px rgba(5,20,75,0.2); }
                .pill-icon { width: 14px; height: 14px; display: flex; align-items: center; justify-content: center; }
                .category-pill.active .pill-icon { color: #fdcc0a; }
                .absolute-full { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
                .object-fit-cover { object-fit: cover; }
                .badge-premium { background: linear-gradient(90deg, #05144b 0%, #4c1d95 100%); color: #ffffff; padding: 6px 16px; border-radius: 50px; font-size: 13px; font-weight: 800; display: inline-block; box-shadow: 0 4px 15px rgba(5, 20, 75, 0.3); }
                .glass-card { background: rgba(255, 255, 255, 0.08); backdrop-filter: blur(12px); border: 1px solid rgba(255, 255, 255, 0.1); }
                .bg-navy { background-color: #05144b; }
                .text-navy { color: #05144b; }
                .icon-box-primary { width: 45px; height: 45px; background: #fdcc0a; color: #05144b; display: flex; align-items: center; justify-content: center; border-radius: 12px; font-size: 1.25rem; }
                .bar-accent { width: 40px; height: 4px; background: #fdcc0a; border-radius: 10px; margin-top: 2px; }
                .gallery-item { border: 4px solid white; transition: all 0.4s ease; box-shadow: 0 4px 15px rgba(0,0,0,0.05); }
                .tab-pill-container { background: #f0f3f8; padding: 5px; border-radius: 100px; display: inline-flex; }
                .tab-pill { border: none; background: transparent; padding: 8px 20px; font-size: 14px; font-weight: 700; border-radius: 100px; color: #6c757d; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
                .tab-pill.active { background: linear-gradient(90deg, #05144b 0%, #4c1d95 100%) !important; color: white !important; box-shadow: 0 5px 15px rgba(5,20,75,0.2); }
                .nb-thumb { width: 50px; height: 50px; overflow: hidden; border-radius: 10px; background: #f8f9fa; border: 1px solid #eee; flex-shrink: 0; }
                .nb-thumb img { width: 100%; height: 100%; object-fit: cover; }
                .border-bottom-dashed { border-bottom: 1px dashed #e5e9ef; }
                .sticky-top-layout { position: sticky; top: 100px; }
                .max-h-90 { max-height: 90vh; }
                .max-w-95 { max-width: 95vw; }
                .max-w-600 { max-width: 600px; }
                .feature-icon-box { width: 32px; height: 32px; background: #fff9e6; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0; }
                .hover-zoom { transition: transform 0.5s ease; }
                .card:hover .hover-zoom { transform: scale(1.1); }
                .overlay-light { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(to top, rgba(0,0,0,0.3), transparent); opacity: 0; transition: opacity 0.3s; }
                .gallery-item:hover .overlay-light { opacity: 1; }
                .shadow-navy-hover:hover { box-shadow: 0 15px 35px rgba(5,20,75,0.12) !important; }
                .btn-navy { background: linear-gradient(90deg, #05144b 0%, #4c1d95 100%); color: white; transition: all 0.3s; }
                .btn-navy:hover { opacity: 0.9; color: white; transform: translateY(-2px); box-shadow: 0 5px 15px rgba(5,20,75,0.3); }
                .btn-outline-navy { border: 2px solid #05144b; color: #05144b; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); background: transparent; }
                .btn-outline-navy:hover { background: linear-gradient(90deg, #05144b 0%, #4c1d95 100%); border-color: transparent; color: white; transform: translateY(-3px); box-shadow: 0 8px 25px rgba(5, 20, 75, 0.2); }
                .shimmer { background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
                @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
                .fixed-full { position: fixed; top: 0; left: 0; right: 0; bottom: 0; width: 100vw; height: 100vh; }
                .z-peak { z-index: 9999; }
                .btn-close-gallery { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: white; width: 45px; height: 45px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; transition: all 0.3s ease; backdrop-filter: blur(5px); }
                .btn-close-gallery:hover { background: #fdcc0a; color: black; border-color: #fdcc0a; transform: rotate(90deg); }
                .gallery-nav-btn { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(5, 20, 75, 0.85); border: 1px solid rgba(255, 255, 255, 0.2); color: white; width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 24px; transition: all 0.3s ease; z-index: 100; backdrop-filter: blur(10px); }
                .gallery-nav-btn:hover { background: #fdcc0a; color: black; border-color: #fdcc0a; scale: 1.1; }
                .prev-btn { left: 40px; }
                .next-btn { right: 40px; }
                @media (max-width: 768px) {
                    .gallery-nav-btn { width: 45px; height: 45px; font-size: 18px; }
                    .prev-btn { left: 10px; }
                    .next-btn { right: 10px; }
                }
            `}</style>
        </div>
    )
}

function LocalityProjectCard({ project: p, wishlistIds = [], onToggleWishlist, onCompare }: { project: Project, wishlistIds?: string[], onToggleWishlist?: any, onCompare?: any }) {
    return (
        <div className="col-md-6">
            <Link to={`/project-details/${p.slug || p._id}`} className="text-decoration-none">
                <motion.div 
                    whileHover={{ y: -10 }}
                    className="card border-0 rounded-xl overflow-hidden h-100 shadow-sm shadow-navy-hover transition-all position-relative"
                    style={{ background: '#fff', border: '1px solid #f1f4f9' }}
                >
                    {/* Image with Price Banner */}
                    <div className="position-relative overflow-hidden" style={{ height: '260px' }}>
                        <img src={getImageUrl(p.featuredImage || p.images?.[0] || '')} alt={p.name} className="w-100 h-100 object-fit-cover transition-transform hover-zoom" />
                        
                        {/* Status Badge & Actions */}
                        <div className="position-absolute top-0 end-0 p-3 d-flex flex-column align-items-end gap-2" style={{ zIndex: 10 }}>
                            <span className="badge bg-white text-navy fw-black px-3 py-2 rounded-xl shadow-sm mb-1" style={{ fontSize: '10px' }}>
                                {p.status || 'Under Construction'}
                            </span>
                            <button 
                                onClick={(e) => onToggleWishlist && onToggleWishlist(e, p._id, 'project')}
                                className={`w-[30px] h-[30px] rounded-full flex items-center justify-center transition-all shadow-[0_4px_10px_rgba(0,0,0,0.3)] border ${wishlistIds?.includes(p._id) ? 'bg-[#f1ae24] text-black border-[#f1ae24]' : 'bg-[#2a2a2a] text-white border-[#444] hover:bg-[#f1ae24] hover:text-black'}`}
                                title="Save Project"
                            >
                                <i className={`${wishlistIds?.includes(p._id) ? 'fa-solid' : 'fa-regular'} fa-heart text-[12px]`}></i>
                            </button>
                            <button
                                onClick={(e) => onCompare && onCompare(e, p)}
                                className="w-[30px] h-[30px] rounded-full bg-[#2a2a2a] text-white flex items-center justify-center hover:bg-[#f1ae24] hover:text-black transition-all shadow-[0_4px_10px_rgba(0,0,0,0.3)] border border-[#444]"
                                title="Add to Compare"
                            >
                                <i className="fa-solid fa-code-compare text-[11px]"></i>
                            </button>
                        </div>

                        {/* Price Banner Overlay */}
                        <div className="position-absolute bottom-0 start-0 p-3 w-100" style={{ background: 'linear-gradient(to top, rgba(5,20,75,0.8), transparent)' }}>
                            <div className="price-banner-premium px-3 py-2 rounded-xl d-inline-block shadow-navy" style={{ background: '#05144b', borderLeft: '4px solid #fdcc0a' }}>
                                <span className="text-white fw-bold" style={{ fontSize: '14px' }}>
                                    {p.overview?.priceRange || p.priceRange || 'On Request'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-4">
                        <h4 className="fw-black text-black mb-1">{p.name}</h4>
                        <p className="text-navy small mb-4 fw-bold opacity-75" style={{ fontSize: '10px' }}>
                            {p.developer?.name || 'By Reputed Developer'}
                        </p>

                        <div className="feature-vertical-list mb-4">
                            <div className="d-flex align-items-center mb-3">
                                <div className="feature-icon-box me-3"><i className="fa-solid fa-bed text-warning"></i></div>
                                <div className="text-black fw-bold small">
                                    {p.overview?.configuration 
                                        ? (p.overview.configuration.toLowerCase().includes('bhk') 
                                            ? p.overview.configuration 
                                            : `${p.overview.configuration} BHK`)
                                        : '2 & 3 BHK Apartments'}
                                </div>
                            </div>
                            <div className="d-flex align-items-center mb-3">
                                <div className="feature-icon-box me-3"><i className="fa-solid fa-maximize text-warning"></i></div>
                                <div className="text-black fw-bold small">Available from 1250 Sq.Ft</div>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="feature-icon-box me-3"><i className="fa-solid fa-location-dot text-warning"></i></div>
                                <div className="text-black fw-bold small text-truncate">{p.location || 'Gandhinagar'}</div>
                            </div>
                        </div>

                        <hr className="opacity-10 mb-4" />

                        {/* Full Width Button */}
                        <div className="btn btn-navy w-100 rounded-pill py-3 fw-black d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}>
                            View Details <i className="fa-solid fa-arrow-right ms-2 fs-xs"></i>
                        </div>
                    </div>
                </motion.div>
            </Link>
        </div>
    )
}

function LocalityListingCard({ property: p, wishlistIds = [], onToggleWishlist }: { property: Property, wishlistIds?: string[], onToggleWishlist?: any }) {
    const formatPrice = (price: number) => {
        if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`
        if (price >= 100000) return `₹${(price / 100000).toFixed(2)} Lac`
        return `₹${price.toLocaleString()}`
    }
    return (
        <div className="col-md-6">
            <Link to={`/property-details/${p._id}`} className="text-decoration-none">
                <motion.div 
                    whileHover={{ y: -10 }} 
                    className="card border-0 rounded-xl overflow-hidden h-100 shadow-sm shadow-navy-hover transition-all position-relative"
                    style={{ background: '#fff', border: '1px solid #f1f4f9' }}
                >
                    {/* Image with Price Banner */}
                    <div className="position-relative overflow-hidden" style={{ height: '220px' }}>
                        <img src={getImageUrl(p.images?.[0])} alt={p.title} className="w-100 h-100 object-fit-cover" />
                        
                        {/* Status Badge & Actions */}
                        <div className="position-absolute top-0 end-0 p-3 d-flex flex-column align-items-end gap-2" style={{ zIndex: 10 }}>
                            <span className="badge bg-white text-navy fw-black px-3 py-2 rounded-xl shadow-sm mb-1" style={{ fontSize: '10px' }}>
                                {p.status || 'Ready to Move'}
                            </span>
                            <button 
                                onClick={(e) => onToggleWishlist && onToggleWishlist(e, p._id, 'property')}
                                className={`w-[30px] h-[30px] rounded-full flex items-center justify-center transition-all shadow-[0_4px_10px_rgba(0,0,0,0.3)] border ${wishlistIds?.includes(p._id) ? 'bg-[#f1ae24] text-black border-[#f1ae24]' : 'bg-[#2a2a2a] text-white border-[#444] hover:bg-[#f1ae24] hover:text-black'}`}
                                title="Save Property"
                            >
                                <i className={`${wishlistIds?.includes(p._id) ? 'fa-solid' : 'fa-regular'} fa-heart text-[12px]`}></i>
                            </button>
                        </div>

                        {/* Price Banner Overlay */}
                        <div className="position-absolute bottom-0 start-0 p-2 w-100" style={{ background: 'linear-gradient(to top, rgba(5,20,75,0.7), transparent)' }}>
                            <div className="price-banner-slim px-3 py-1 rounded-xl d-inline-block shadow-navy" style={{ background: '#05144b', borderLeft: '3px solid #fdcc0a' }}>
                                <span className="text-white fw-bold" style={{ fontSize: '13px' }}>
                                    {formatPrice(p.price)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-4">
                        <h5 className="fw-black text-black mb-1 text-truncate">{p.title}</h5>
                        <p className="text-navy small mb-4 fw-bold opacity-75" style={{ fontSize: '10px' }}>Featured Listing</p>

                        {/* Vertical Feature List */}
                        <div className="feature-vertical-list mb-4">
                            <div className="d-flex align-items-center mb-3">
                                <div className="feature-icon-box me-3"><i className="fa-solid fa-vector-square text-warning"></i></div>
                                <div className="text-black fw-bold small">Prime Configuration</div>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="feature-icon-box me-3"><i className="fa-solid fa-map-pin text-warning"></i></div>
                                <div className="text-black fw-bold small text-truncate opacity-75">{p.address}</div>
                            </div>
                        </div>

                        <div className="btn btn-navy w-100 rounded-pill py-3 fw-black uppercase ls-1 d-flex align-items-center justify-content-center">
                            See Details <i className="fa-solid fa-arrow-right ms-2 fs-xs"></i>
                        </div>
                    </div>
                </motion.div>
            </Link>
        </div>
    )
}

function LoadingCards() {
    return (
        <div className="row g-4">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="col-md-6">
                    <div className="card border-0 rounded-xl overflow-hidden shadow-navy">
                        <div className="bg-light shimmer" style={{ height: '180px' }}></div>
                        <div className="card-body p-4">
                            <div className="bg-light rounded-pill mb-2 shimmer" style={{ height: '20px', width: '70%' }}></div>
                            <div className="bg-light rounded-pill mb-4 shimmer" style={{ height: '12px', width: '40%' }}></div>
                            <div className="bg-light rounded-xl shimmer" style={{ height: '40px', width: '100%' }}></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

function FullPageLoading() {
    return (
        <>
            <Navbar transparent={false} />
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
                <div className="text-center">
                    <div className="spinner-grow text-primary mb-3" role="status"></div>
                    <h5 className="fw-bold text-navy">Curating Experience...</h5>
                </div>
            </div>
            <Footer />
        </>
    )
}

function NotFoundState() {
    return (
        <>
            <Navbar transparent={false} />
            <div className="container py-5 text-center" style={{ minHeight: '60vh' }}>
                <div className="mb-4">
                    <i className="fa-solid fa-map-location-dot fa-5x text-black fw-bold opacity-25"></i>
                </div>
                <h2 className="fw-bold text-navy mb-3">Location Not Found</h2>
                <p className="text-black fw-bold mb-4 mx-auto" style={{ maxWidth: '400px' }}>The locality you are looking for might have been moved or doesn't exist.</p>
                <Link to="/" className="btn btn-navy rounded-pill px-5 py-3 fw-bold">Return Home</Link>
            </div>
            <Footer />
        </>
    )
}

function EmptyState({ message }: { message: string }) {
    return (
        <div className="text-center py-5 bg-white rounded-xl border border-dashed my-2 col-12">
            <i className="fa-solid fa-hotel fa-3x text-black fw-bold opacity-25 mb-3"></i>
            <h6 className="fw-bold text-black fw-bold">{message}</h6>
        </div>
    )
}
